/* eslint-disable spaced-comment */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Spinner } from '@agconnections/grow-ui';

import {
  useAuth,
  coreServiceAutoOrgPermissionChecker
} from 'utilities/base-auth';
import useOrganization from 'hooks/useOrganization';
import useOrgWorkspaceId from 'hooks/useOrgWorkspaceId';
import Navigation from 'components/Navigation';
import ErrorHandlerWithRouter from 'components/ErrorHandler';
import setOrgAndPermission from 'helpers/setOrgAndPermission';
import { Context } from 'components/Store';
import { IS_FULL_SCREEN_NEEDED } from 'reducers/reducer';
import getPreviousValue from 'helpers/findPreviousValue';
import { paths } from 'routes/paths';
import { useToast } from 'components/ToastContainer';
import useOrganizations from 'hooks/useOrganizations';
import OrgGlobalLoader from 'components/OrgGlobalLoader';
import OrgApiFailure from 'components/ErrorHandler/OrganizationApiFailure/OrgApiFailure';
import useInvoiceFlags from 'screens/Invoices/redesign/hooks/useInvoiceFlags';
import usePlansFlags from 'screens/Plans/redesign/hooks/usePlansFlags';
import {
  clearOrganizationReloadAttempts,
  clearSelectedOrganization,
  getSelectedOrganizationId
} from 'utilities/selectedOrganization';
import TopNavigation from './components/TopNavigation';
import './index.css';

const returnData = (value1, value2) => {
  if (value1) return value1;
  if (value2) return [value2];
  return [];
};

const FULL_SCREEN_PAGES = [
  'property-landing',
  'applications/create',
  'applications',
  'work-orders',
  'invoices',
  'reports',
  'crop-zone-matcher',
  'products',
  'storage-locations',
  'sale-locations',
  'loads',
  'yieldV2/create',
  'yieldV2/commodity-summary'
];

// Pages that go full-screen on an EXACT pathname match only. The invoices
// redesigned landing is full-screen, but the legacy create/edit/view sub-routes
// (`/app/invoices/:id`, `/:id/view`) rely on the AppShell `p-4` padding (their
// `-mx-4` header breaks out of it), so they must NOT match.
const FULL_SCREEN_EXACT_PAGES = new Set([paths.invoices]);

const EMBEDDED_ROUTES = [
  `${paths.yieldV2}/storage-locations`,
  `${paths.yieldV2}/sale-locations`,
  `${paths.yieldV2}/loads`,
  `${paths.yieldV2}/commodity-summary`
];

const ROUTES_TO_EXCLUDE = ['/app/welcome'];

const SCROLLBAR_ROUTE_CONFIGS = [
  { path: '/app/crop-seasons', className: 'config-scrollbar-crop-seasons' },
  { path: '/app/organizations', className: 'config-scrollbar-organizations' },
  {
    path: '/app/property-landing',
    className: 'config-scrollbar-property-landing'
  },
  { path: '/app/work-orders', className: 'config-scrollbar-work-orders' },
  { path: '/app/applications', className: 'config-scrollbar-applications' }
];

const SCROLLBAR_CLASSES = SCROLLBAR_ROUTE_CONFIGS.map(
  ({ className }) => `.${className}`
);
const SCROLLBAR_WEBKIT_SELECTORS = SCROLLBAR_CLASSES.flatMap(className => [
  `${className}::-webkit-scrollbar`,
  `${className} *::-webkit-scrollbar`
]);
const SCROLLBAR_WEBKIT_TRACK_SELECTORS = SCROLLBAR_CLASSES.flatMap(
  className => [
    `${className}::-webkit-scrollbar-track`,
    `${className} *::-webkit-scrollbar-track`
  ]
);
const SCROLLBAR_WEBKIT_THUMB_SELECTORS = SCROLLBAR_CLASSES.flatMap(
  className => [
    `${className}::-webkit-scrollbar-thumb`,
    `${className} *::-webkit-scrollbar-thumb`
  ]
);
const SCROLLBAR_WEBKIT_THUMB_HOVER_SELECTORS = SCROLLBAR_CLASSES.flatMap(
  className => [
    `${className}::-webkit-scrollbar-thumb:hover`,
    `${className} *::-webkit-scrollbar-thumb:hover`
  ]
);
const SCROLLBAR_FIREFOX_SELECTORS = SCROLLBAR_CLASSES.flatMap(className => [
  className,
  `${className} *`
]);

const AppShell = ({ routes, children = null }) => {
  const [
    { organization, isFullScreenNeeded, loadingOrg: LoadingOrgAndPermission },
    dispatch
  ] = useContext(Context);
  const { user } = useAuth();
  const toast = useToast();
  const { getDefaultOrg, loadedDefaultOrg, defaultOrg } = useOrganizations();
  const {
    setOrganization,
    organizationPermissionError,
    getOrgByIdV1,
    loadedOrgByIdV1,
    orgByIdV1
  } = useOrganization();
  const { getOrgWorkspaceId } = useOrgWorkspaceId();
  const [stateLoaded, setStateLoaded] = useState(false);
  const [error, setError] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const { releaseFe50Orgs } = useFlags();
  const { feInvoicesActive } = useInvoiceFlags();
  const { fePlansActive } = usePlansFlags();
  const redirectToWelcome = !user;
  const APILoaded = loadedDefaultOrg || loadedOrgByIdV1;
  const orgIds = useRef(new Set());
  const validateLoop = useRef('');
  const bootstrapTried = useRef(false);
  const staleOrgRetried = useRef(false);

  const pushToWelcome = () => {
    history.replace(paths.welcome);
    setStateLoaded(true);
    orgIds.current = new Set();
    validateLoop.current = '';
  };

  const pushToOrgApiFailure = () => {
    setError(true);
  };

  // New users get an auto-created org but no CWF roles, so the orgs endpoint
  // returns nothing. Grant the roles once here, right after the orgs load; the
  // caller then retries loading the orgs so the now-permitted org resolves.
  // Best-effort — failures are logged and the retry falls through to the
  // existing org API failure handling.
  const grantCwfRoles = async currentUser => {
    try {
      const workspaceId = await getOrgWorkspaceId(
        currentUser.auto_created_org_id
      );
      if (workspaceId) {
        await coreServiceAutoOrgPermissionChecker(
          currentUser.auto_created_org_id,
          workspaceId,
          currentUser.id
        );
      } else {
        datadogLogs.logger.error('No workspace id found for auto-created org', {
          userId: currentUser.id,
          autoCreatedOrgId: currentUser.auto_created_org_id
        });
      }
    } catch (err) {
      datadogLogs.logger.error('Error granting CWF roles to auto-created org', {
        error: err,
        userId: currentUser.id,
        autoCreatedOrgId: currentUser.auto_created_org_id
      });
      // swallow the error so the promise doesn't reject unhandled
    }
  };

  useEffect(() => {
    if (!releaseFe50Orgs) {
      getDefaultOrg(0, 50);
      return;
    }
    const selectedOrganizationId = getSelectedOrganizationId(user?.id);

    if (selectedOrganizationId) {
      getOrgByIdV1(selectedOrganizationId);
      return;
    }
    getDefaultOrg(0, 1);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isOrgFailingAPI = organizationPermissionError;
    if (!releaseFe50Orgs && isOrgFailingAPI) {
      toast.error('Unable to load organization data', {
        supportButton: true,
        content:
          "An error prevented us from loading your list of organization's. Please try again."
      });
      pushToOrgApiFailure();
    }
    if (!releaseFe50Orgs || !isOrgFailingAPI) return;

    if (orgIds.current.size <= 1) {
      toast.error('Unable to load organization data', {
        supportButton: true,
        content:
          "An error prevented us from loading your list of organization's. Please try again."
      });
      pushToOrgApiFailure();
      return;
    }

    const previousSelectedOrg = getPreviousValue(orgIds.current);
    if (validateLoop.current === previousSelectedOrg) {
      toast.error('Unable to load organization data', {
        supportButton: true,
        content:
          "An error prevented us from loading any of your organization's. Please try logging in again."
      });
      pushToOrgApiFailure();
      return;
    }
    toast.error('Unable to load organization data', {
      supportButton: true,
      content: `An error prevented us from loading your organization ${organization.name}. Please try again.`
    });
    validateLoop.current = previousSelectedOrg;
    getOrgByIdV1(previousSelectedOrg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationPermissionError]);

  useEffect(() => {
    if (!APILoaded) {
      return;
    }
    if (redirectToWelcome && location.pathname !== paths.welcome) {
      pushToWelcome();
      return;
    }
    if (!orgByIdV1 && !defaultOrg?.length) {
      if (user?.auto_created_org_id && !bootstrapTried.current) {
        bootstrapTried.current = true;
        // Grant the roles, then retry loading the orgs so the now-permitted
        // auto-created org resolves. bootstrapTried guards against a loop — a
        // still-empty retry falls through to the failure handling below.
        grantCwfRoles(user).then(() =>
          getDefaultOrg(0, releaseFe50Orgs ? 1 : 50)
        );
        return;
      }
      // The stored organization no longer loads - it was deleted, access was
      // revoked, or a different user signed in on this tab. Drop the stale
      // selection and fall back to the default org rather than stranding the
      // user on the org API failure screen.
      if (
        releaseFe50Orgs &&
        getSelectedOrganizationId() &&
        !staleOrgRetried.current
      ) {
        staleOrgRetried.current = true;
        clearSelectedOrganization();
        getDefaultOrg(0, 1);
        return;
      }
      pushToOrgApiFailure();
      return;
    }

    clearOrganizationReloadAttempts();
    setOrgAndPermission(
      user,
      // getOrganizationData(organizationV2, organizationsV2),
      returnData(defaultOrg, orgByIdV1),
      dispatch,
      setStateLoaded
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [APILoaded, defaultOrg, orgByIdV1]);

  // The effect above cannot resolve the stale-selection retry on its own: an
  // empty retry leaves `defaultOrg` at the value it already had, so no
  // dependency changes and it never re-runs. Terminate the retry here instead,
  // once the fallback fetch has settled with nothing to show.
  useEffect(() => {
    if (staleOrgRetried.current && loadedDefaultOrg && !defaultOrg?.length) {
      pushToOrgApiFailure();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedDefaultOrg, defaultOrg]);

  useEffect(() => {
    const path = location.pathname.split('/');
    // Only the redesigned create/edit/duplicate screens are full-screen. When
    // the flag is off these paths render the legacy Invoice, which relies on
    // the AppShell `p-4` padding (its `-mx-4` header breaks out of it
    // otherwise).
    const isInvoiceRedesignRoute =
      feInvoicesActive &&
      /\/invoices\/(create|[^/]+\/(edit|duplicate))$/.test(location.pathname);
    // The redesigned Plans landing is the entire `/app/plans` page (no
    // separate create/edit/duplicate routes like Invoices), so this only
    // needs an exact-pathname check, gated on the flag the same way so the
    // legacy landing (which still relies on the `p-4` padding) keeps it.
    const isPlansRedesignRoute =
      fePlansActive &&
      (location.pathname === paths.plans ||
        /\/plans\/(create|[^/]+\/(edit))$/.test(location.pathname));
    const payload =
      FULL_SCREEN_EXACT_PAGES.has(location.pathname) ||
      isInvoiceRedesignRoute ||
      isPlansRedesignRoute ||
      FULL_SCREEN_PAGES.some(page => {
        const isCompositePath = page.includes('/');
        if (isCompositePath) return location.pathname.includes(page);
        return path.includes(page);
      });
    dispatch({
      type: IS_FULL_SCREEN_NEEDED,
      payload
    });
  }, [location.pathname, dispatch, feInvoicesActive, fePlansActive]);

  useEffect(() => {
    if (organization.id !== "7b6ca193-5144-4835-a292-be30f744492b") {
      orgIds.current.add(organization.id);
      setOrganization(organization);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const getScrollbarClass = () => {
    return (
      SCROLLBAR_ROUTE_CONFIGS.find(({ path }) =>
        location.pathname.includes(path)
      )?.className ?? ''
    );
  };

  // Keep route-specific scrollbar styling inside the main content area.
  useEffect(() => {
    const shouldApplyScrollbarClass = SCROLLBAR_ROUTE_CONFIGS.some(({ path }) =>
      location.pathname.includes(path)
    );

    if (shouldApplyScrollbarClass) {
      // Create or update inline style to override global scrollbar
      // This ensures our styles load last and take precedence
      let styleElement = document.getElementById('config-scrollbar-override');
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'config-scrollbar-override';
        document.head.appendChild(styleElement);
      }

      styleElement.textContent = `
        ${SCROLLBAR_WEBKIT_SELECTORS.join(',\n')} {
          height: 8px !important;
          width: 8px !important;
          background: #fff !important;
        }
        ${SCROLLBAR_WEBKIT_TRACK_SELECTORS.join(',\n')} {
          background: #fff !important;
        }
        ${SCROLLBAR_WEBKIT_THUMB_SELECTORS.join(',\n')} {
          background: #888 !important;
          border-radius: 4px !important;
        }
        ${SCROLLBAR_WEBKIT_THUMB_HOVER_SELECTORS.join(',\n')} {
          background: #555 !important;
        }

        ${SCROLLBAR_FIREFOX_SELECTORS.join(',\n')} {
          scrollbar-width: thin !important;
          scrollbar-color: #888 #fff !important;
        }
      `;
    } else {
      // Remove the override style when not needed
      const styleElement = document.getElementById('config-scrollbar-override');
      if (styleElement) {
        styleElement.remove();
      }
    }

    // Remove the route-specific scrollbar overrides when the route changes or unmounts.
    return () => {
      const styleElement = document.getElementById('config-scrollbar-override');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [location.pathname]);

  return (
    <>
      {error ? (
        <OrgApiFailure />
      ) : (
        <div className="flex h-screen bg-[#F8F9FA]">
          <Navigation routes={routes} />
          <div className="flex flex-col flex-1 w-0">
            {stateLoaded ? (
              <>
                <TopNavigation />
                <main
                  className={`relative overflow-auto flex flex-1 focus:outline-hidden bg-app-background ${getScrollbarClass()}`}
                  style={{ height: 'calc(100% - 64px)' }}
                >
                  <div
                    className={`w-full flex flex-col flex-1 ${!isFullScreenNeeded &&
                      'p-4'} mx-auto`}
                  >
                    <ErrorHandlerWithRouter>{children}</ErrorHandlerWithRouter>
                    {releaseFe50Orgs && (
                      <OrgGlobalLoader
                        route={location.pathname}
                        shouldRenderInRoutes={[
                          ...Object.values(paths),
                          ...EMBEDDED_ROUTES
                        ].filter(route => !ROUTES_TO_EXCLUDE.includes(route))}
                        isLoading={LoadingOrgAndPermission}
                      />
                    )}
                  </div>
                </main>
              </>
            ) : (
              <Spinner />
            )}
          </div>
        </div>
      )}
    </>
  );
};

AppShell.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  children: PropTypes.element
};

export default AppShell;
